/*
 * cart-quantity-limit.js
 * ----------------------
 * Caps how many units of a single variant a customer can have in the cart.
 *
 * Why this is needed: products with inventory tracking turned OFF have no
 * stock cap, so Shopify happily accepts a quantity of 1,000,000.
 *
 * What it does:
 *   1. Sets max="50" on every quantity field and clamps typed values.
 *   2. Intercepts the theme's AJAX cart calls (/cart/add.js, /cart/change.js,
 *      /cart/update.js) and clamps the quantity before the request is sent.
 *      For /cart/add.js it also checks what is ALREADY in the cart, so a
 *      customer cannot add 50, then 50 more.
 *   3. Clamps classic (non-AJAX) form submissions to /cart/add.
 *
 * Change MAX_QTY below if you want a different cap.
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ */
  /* CONFIG                                                              */
  /* ------------------------------------------------------------------ */
  var MAX_QTY = 50;
  var MESSAGE = 'You can add up to ' + MAX_QTY + ' of this item per order.';

  /* ------------------------------------------------------------------ */
  /* SETUP                                                               */
  /* ------------------------------------------------------------------ */
  var ROOT =
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  var nativeFetch = window.fetch ? window.fetch.bind(window) : null;
  if (!nativeFetch) return; // very old browser, nothing to patch

  var QTY_SELECTOR = 'input[name="quantity"], input[name^="updates"]';

  /* ------------------------------------------------------------------ */
  /* SMALL ON-SCREEN MESSAGE                                             */
  /* ------------------------------------------------------------------ */
  function showMessage(text) {
    var el = document.getElementById('cart-qty-limit-toast');
    if (!el) {
      el = document.createElement('div');
      el.id = 'cart-qty-limit-toast';
      el.setAttribute('role', 'status');
      el.style.cssText =
        'position:fixed;left:50%;bottom:24px;transform:translateX(-50%);' +
        'z-index:2147483000;background:#111;color:#fff;padding:12px 18px;' +
        'border-radius:6px;font-size:14px;line-height:1.3;max-width:90vw;' +
        'text-align:center;opacity:0;pointer-events:none;' +
        'transition:opacity .2s ease;';
      document.body.appendChild(el);
    }
    el.textContent = text;
    requestAnimationFrame(function () {
      el.style.opacity = '1';
    });
    clearTimeout(el._hideTimer);
    el._hideTimer = setTimeout(function () {
      el.style.opacity = '0';
    }, 4000);
  }

  /* ------------------------------------------------------------------ */
  /* HELPERS                                                             */
  /* ------------------------------------------------------------------ */
  function urlOf(input) {
    if (typeof input === 'string') return input;
    if (input && typeof input.url === 'string') return input.url;
    return '';
  }

  function hitsCartEndpoint(input, name) {
    return urlOf(input).split('?')[0].indexOf('/cart/' + name) !== -1;
  }

  // Returns { "<variant_id>": quantityAlreadyInCart }
  function getCartQuantities() {
    return nativeFetch(ROOT + 'cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        var map = {};
        (cart.items || []).forEach(function (item) {
          var key = String(item.variant_id);
          map[key] = (map[key] || 0) + item.quantity;
        });
        return map;
      })
      .catch(function () {
        return {}; // if this fails, fall back to a plain per-request cap
      });
  }

  function parseBody(body) {
    if (!body) return null;
    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      return { kind: 'form', data: body };
    }
    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      return { kind: 'form', data: body };
    }
    if (typeof body === 'string') {
      try {
        return { kind: 'json', data: JSON.parse(body) };
      } catch (e) {
        return { kind: 'form', data: new URLSearchParams(body) };
      }
    }
    return null; // Blob / ReadableStream etc. — leave untouched
  }

  function serializeBody(parsed, originalBody) {
    if (parsed.kind === 'json') return JSON.stringify(parsed.data);
    if (typeof originalBody === 'string') return parsed.data.toString();
    return parsed.data; // FormData or URLSearchParams, mutated in place
  }

  function toInt(value, fallback) {
    var n = parseInt(value, 10);
    return isNaN(n) ? fallback : n;
  }

  /* ------------------------------------------------------------------ */
  /* CLAMP /cart/add.js  (quantity is ADDED to what's already there)     */
  /* ------------------------------------------------------------------ */
  function clampAdd(parsed) {
    return getCartQuantities().then(function (inCart) {
      var clamped = false;

      function room(variantId, requested) {
        var already = inCart[String(variantId)] || 0;
        var allowed = Math.max(0, MAX_QTY - already);
        if (requested > allowed) {
          clamped = true;
          return allowed;
        }
        return requested;
      }

      if (parsed.kind === 'form') {
        var id = parsed.data.get('id');
        if (!id) return { clamped: false, blockAll: false };
        var qty = room(id, toInt(parsed.data.get('quantity'), 1));
        parsed.data.set('quantity', String(qty));
        return { clamped: clamped, blockAll: qty <= 0 };
      }

      var d = parsed.data;

      if (Array.isArray(d.items)) {
        d.items = d.items
          .map(function (item) {
            item.quantity = room(item.id, toInt(item.quantity, 1));
            return item;
          })
          .filter(function (item) {
            return item.quantity > 0;
          });
        return { clamped: clamped, blockAll: d.items.length === 0 };
      }

      if (d.id !== undefined) {
        d.quantity = room(d.id, toInt(d.quantity, 1));
        return { clamped: clamped, blockAll: d.quantity <= 0 };
      }

      return { clamped: false, blockAll: false };
    });
  }

  /* ------------------------------------------------------------------ */
  /* CLAMP /cart/change.js and /cart/update.js (quantity is ABSOLUTE)    */
  /* ------------------------------------------------------------------ */
  function clampChange(parsed) {
    var clamped = false;

    if (parsed.kind === 'form') {
      var q = toInt(parsed.data.get('quantity'), null);
      if (q !== null && q > MAX_QTY) {
        parsed.data.set('quantity', String(MAX_QTY));
        clamped = true;
      }
      var keysToFix = [];
      parsed.data.forEach(function (value, key) {
        if (key.indexOf('updates') === 0 && toInt(value, 0) > MAX_QTY) {
          keysToFix.push(key);
        }
      });
      keysToFix.forEach(function (key) {
        parsed.data.set(key, String(MAX_QTY));
        clamped = true;
      });
      return clamped;
    }

    var d = parsed.data;

    if (d.quantity !== undefined && toInt(d.quantity, 0) > MAX_QTY) {
      d.quantity = MAX_QTY;
      clamped = true;
    }

    if (d.updates) {
      if (Array.isArray(d.updates)) {
        d.updates = d.updates.map(function (v) {
          if (toInt(v, 0) > MAX_QTY) {
            clamped = true;
            return MAX_QTY;
          }
          return v;
        });
      } else if (typeof d.updates === 'object') {
        Object.keys(d.updates).forEach(function (k) {
          if (toInt(d.updates[k], 0) > MAX_QTY) {
            d.updates[k] = MAX_QTY;
            clamped = true;
          }
        });
      }
    }

    return clamped;
  }

  /* ------------------------------------------------------------------ */
  /* PATCH window.fetch                                                  */
  /* ------------------------------------------------------------------ */
  function refusedResponse() {
    return new Response(
      JSON.stringify({
        status: 422,
        message: 'Cart Error',
        description: MESSAGE
      }),
      { status: 422, headers: { 'Content-Type': 'application/json' } }
    );
  }

  window.fetch = function (input, init) {
    init = init || {};

    var isAdd = hitsCartEndpoint(input, 'add');
    var isChange =
      hitsCartEndpoint(input, 'change') || hitsCartEndpoint(input, 'update');

    if (!isAdd && !isChange) return nativeFetch(input, init);

    var originalBody = init.body;
    var parsed = parseBody(originalBody);
    if (!parsed) return nativeFetch(input, init);

    if (isAdd) {
      return clampAdd(parsed).then(function (result) {
        if (result.clamped || result.blockAll) showMessage(MESSAGE);
        if (result.blockAll) return refusedResponse();
        init.body = serializeBody(parsed, originalBody);
        return nativeFetch(input, init);
      });
    }

    if (clampChange(parsed)) showMessage(MESSAGE);
    init.body = serializeBody(parsed, originalBody);
    return nativeFetch(input, init);
  };

  /* ------------------------------------------------------------------ */
  /* QUANTITY INPUTS: set max + clamp typed values                       */
  /* ------------------------------------------------------------------ */
  function capInputs() {
    var inputs = document.querySelectorAll(QTY_SELECTOR);
    for (var i = 0; i < inputs.length; i++) {
      var input = inputs[i];
      var currentMax = toInt(input.getAttribute('max'), null);
      if (currentMax === null || currentMax > MAX_QTY) {
        input.setAttribute('max', String(MAX_QTY));
      }
      if (toInt(input.value, 0) > MAX_QTY) input.value = MAX_QTY;
    }
  }

  var capTimer = null;
  function scheduleCap() {
    clearTimeout(capTimer);
    capTimer = setTimeout(capInputs, 100);
  }

  function init() {
    capInputs();

    if (window.MutationObserver && document.body) {
      new MutationObserver(scheduleCap).observe(document.body, {
        childList: true,
        subtree: true
      });
    }

    document.addEventListener('change', function (e) {
      var t = e.target;
      if (!t || !t.matches || !t.matches(QTY_SELECTOR)) return;
      if (toInt(t.value, 0) > MAX_QTY) {
        t.value = MAX_QTY;
        showMessage(MESSAGE);
      }
    });

    // Classic non-AJAX product form
    document.addEventListener(
      'submit',
      function (e) {
        var form = e.target;
        if (!form || !form.action || form.action.indexOf('/cart/add') === -1) return;
        var q = form.querySelector('input[name="quantity"]');
        if (q && toInt(q.value, 0) > MAX_QTY) {
          q.value = MAX_QTY;
          showMessage(MESSAGE);
        }
      },
      true
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();